import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FormProvider, useForm, type SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import {
  authenticationStateVar,
  setAuthenticationState,
} from "@/apollo/auth-state";
import { AdminPageShell } from "@/components/admin-page-shell";
import { FieldGroupSurface } from "@/components/field-group-surface";
import { PageIntro } from "@/components/page-intro";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  CREATE_INSTAGRAM,
  type CreateInstagramData,
  type CreateInstagramVariables,
} from "@/graphql";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { analytics } from "@/lib/analytics";

const createSchema = z.object({
  instagramUsername: z
    .string()
    .min(1, "Instagram username is required")
    .regex(
      /^[a-zA-Z0-9_.]*$/,
      "Must be a valid instagram username (don't include @)"
    ),
  migrate: z.boolean(),
});

type CreateFormValues = z.infer<typeof createSchema>;

declare global {
  interface Window {
    _hsq?: Array<unknown>;
    _ctrack?: { track: (name: string) => void };
    Intercom?: (event: string, name: string) => void;
  }
}

export function CreatePage() {
  useDocumentTitle("Add Instagram account");
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createInstagram] = useMutation<
    CreateInstagramData,
    CreateInstagramVariables
  >(CREATE_INSTAGRAM);

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { instagramUsername: "", migrate: false },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<CreateFormValues> = async (values) => {
    setErrorMessage(null);
    try {
      await createInstagram({
        variables: {
          username: values.instagramUsername,
          migrate: values.migrate,
          migrateFrom: "linktree",
        },
      });

      const current = authenticationStateVar();
      const accounts = current.accounts.includes(values.instagramUsername)
        ? current.accounts
        : [...current.accounts, values.instagramUsername];
      setAuthenticationState({
        accounts,
        selectedAccount: values.instagramUsername,
      });

      analytics.track("user.created_instagram", {
        instagramUsername: values.instagramUsername,
      });

      // Best-effort third-party events (preserved from legacy).
      try {
        window._hsq?.push(["trackEvent", { id: "sked_link_created" }]);
        window.Intercom?.("trackEvent", "sked-link-created");
        window._ctrack?.track("sked-link-created");
      } catch {
        // ignore any third-party errors
      }

      navigate("/build");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      setErrorMessage(message);
    }
  };

  const { isSubmitting, isValid } = form.formState;

  return (
    <AdminPageShell className="max-w-4xl">
      <div className="space-y-8">
        <PageIntro
          title="Add Instagram account"
          description="Connect an Instagram username to start building and publishing a Sked Link for that profile."
        />

        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col space-y-6"
          >
            <FieldGroupSurface className="space-y-6">
              <FormField
                control={form.control}
                name="instagramUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instagram username</FormLabel>
                    <FormControl>
                      <Input placeholder="nike" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the part after the @. For example, if you&apos;re
                      @nike, write nike.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="migrate"
                render={({ field }) => (
                  <FormItem className="flex items-start gap-3 space-y-0 rounded-xl border border-neutral-100 bg-neutral-25 p-4">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1">
                      <FormLabel className="m-0">
                        Migrate automatically
                      </FormLabel>
                      <FormDescription>
                        If you&apos;re currently using Linktree, you can try
                        migrating your existing links automatically.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </FieldGroupSurface>

            {errorMessage ? (
              <p className="font-inter text-sm text-red-600">{errorMessage}</p>
            ) : null}

            <Button
              type="submit"
              disabled={isSubmitting || !isValid}
              className="self-end"
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  Adding...
                </>
              ) : (
                "Add account"
              )}
            </Button>
          </form>
        </FormProvider>
      </div>
    </AdminPageShell>
  );
}
